import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Página para seleção de salão quando o usuário tem acesso a múltiplos salões
 * @returns {JSX.Element} Página de seleção de salão
 */
const SalonSelectionPage = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { 
    userSalons, 
    loading, 
    switchSalon, 
    refreshUserData, 
    getCurrentRole 
  } = useUser();
  const [error, setError] = useState(null);

  // Verificar se o usuário está autenticado
  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = async () => {
      if (!loading) {
        if (!currentUser) {
          console.log('[SalonSelectionPage] Usuário não autenticado, redirecionando para login');
          
          // Usar setTimeout para navegação segura
          setTimeout(() => {
            if (isMounted) {
              navigate('/login', { replace: true });
            }
          }, 0);
        } else {
          // Usuário está autenticado, atualizar dados
          console.log('[SalonSelectionPage] Usuário autenticado:', currentUser.uid);
          if (isMounted) {
            await refreshUserData();
          }
        }
      }
    };
    
    checkAuth();
    
    return () => {
      isMounted = false;
    };
  }, [currentUser, loading, navigate, refreshUserData]);

  // Se só tiver um salão, redirecionar automaticamente
  useEffect(() => {
    let isMounted = true;
    
    const redirectIfSingleSalon = () => {
      if (!loading && userSalons.length === 1 && isMounted) {
        console.log('[SalonSelectionPage] Redirecionando para o único salão disponível:', userSalons[0].id);
        
        // Usar setTimeout para navegação segura
        setTimeout(() => {
          if (isMounted) {
            handleSelectSalon(userSalons[0].id);
          }
        }, 0);
      }
    };
    
    redirectIfSingleSalon();
    
    return () => {
      isMounted = false;
    };
  }, [userSalons, loading]);

  // Função para formatar o texto de role
  const formatRole = (role) => {
    const roles = {
      'admin': 'Administrador',
      'owner': 'Proprietário',
      'manager': 'Gestor',
      'professional': 'Profissional'
    };
    
    return roles[role] || role;
  };

  // Função para selecionar um salão
  const handleSelectSalon = (salonId) => {
    if (switchSalon(salonId)) {
      // Verificar o papel do usuário no salão selecionado
      const salon = userSalons.find(s => s.id === salonId);
      if (salon && salon.role) {
        console.log(`[SalonSelectionPage] Salão selecionado: ${salonId}, papel: ${salon.role}`);
        
        // Usar setTimeout para navegação segura
        setTimeout(() => {
          // Redirecionar com base no papel
          if (salon.role === 'professional') {
            navigate('/professional', { replace: true });
          } else if (['owner', 'admin', 'manager'].includes(salon.role)) {
            navigate(`/admin/${salonId}/appointments`, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        }, 0);
      }
    }
  };

  // Função para fazer logout
  const handleLogout = async () => {
    try {
      console.log('[SalonSelectionPage] Fazendo logout');
      const success = await logout();
      if (success) {
        // Usar setTimeout para navegação segura
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 0);
      } else {
        setError('Falha ao fazer logout. Tente novamente.');
      }
    } catch (error) {
      console.error('[SalonSelectionPage] Erro ao fazer logout:', error);
      setError('Erro ao fazer logout. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Selecione um Salão
            </h1>
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                {error}
              </div>
            )}
            
            {userSalons.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-600 mb-4">
                  Você não tem acesso a nenhum salão.
                </p>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200"
                >
                  Voltar para o login
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {userSalons.map((salon) => (
                  <div 
                    key={salon.id}
                    className="border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden cursor-pointer"
                    onClick={() => handleSelectSalon(salon.id)}
                  >
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        {salon.logoUrl ? (
                          <img 
                            src={salon.logoUrl} 
                            alt={salon.name || salon.id} 
                            className="w-12 h-12 rounded-full object-cover mr-4"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                            <span className="text-blue-600 font-bold text-lg">
                              {(salon.name || salon.id).charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {salon.name || salon.id}
                          </h3>
                          <p className="text-sm text-blue-600">
                            {formatRole(salon.role)}
                          </p>
                        </div>
                      </div>
                      
                      {salon.address && (
                        <p className="text-sm text-gray-500 mb-2">
                          {typeof salon.address === 'string' 
                            ? salon.address 
                            : salon.address.formattedAddress || 
                              `${salon.address.street || ''} ${salon.address.number || ''}, ${salon.address.city || ''}`}
                        </p>
                      )}
                      
                      <button
                        className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200"
                      >
                        Acessar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-8 text-center">
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900"
              >
                Sair da conta
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalonSelectionPage; 
 